import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomEncoder, ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers, SortUtil } from '@iot-platform/iot-platform-utils';
import { CommonApiListResponse, CommonApiRequestBody, CommonGenericModel, Environment, Filter } from '@iot-platform/models/common';
import { Dashboard, DashboardWidget } from '@iot-platform/models/dashboards';
import { catchError, forkJoin, map, Observable, of, retry, switchMap } from 'rxjs';

export interface BackendGroupingResponse {
  data: { legend: string; color: string; count: number }[];
  options?: { showLegends: boolean };
}

@Injectable({
  providedIn: 'root'
})
export class DashboardsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly environment: Environment = inject(ENVIRONMENT);

  getDashboardData(dashboard: Dashboard, filters: Filter[]): Observable<DashboardWidget[]> {
    return forkJoin(dashboard.widgets.map((w) => this.getWidgetData(dashboard.concept, w, filters)));
  }

  getWidgetData(concept: string, widget: DashboardWidget, filters: Filter[]): Observable<DashboardWidget> {
    const body: CommonApiRequestBody = ApiHelpers.getHttpRequestBody({
      filters
    });
    body.options = {
      ...widget.requestOptions
    };

    if (widget.options?.preliminaryRequest) {
      let preliminaryRequestParams: HttpParams = new HttpParams({ encoder: new CustomEncoder() });

      if (widget.options.preliminaryRequest.filters) {
        widget.options.preliminaryRequest.filters.forEach((filter) => {
          preliminaryRequestParams = preliminaryRequestParams.append(filter.criteriaKey, filter.value);
        });
      }

      return this.http
        .get<
          CommonApiListResponse<CommonGenericModel>
        >(`${this.environment.api.url}${widget.options.preliminaryRequest.endpoint}`, { params: preliminaryRequestParams })
        .pipe(
          map((response) => response.content),
          switchMap((responseData) => {
            const matchingObject: any = widget.options?.matchingObject;
            if (matchingObject) {
              const found: any = responseData.find((el: any) => el[matchingObject.matchingProperty] === matchingObject.matchingValue);
              if (found) {
                (body as any).options[matchingObject.key] = found[matchingObject.value];
              }
            }
            return this.getFormattedWidgetData(concept, widget, body);
          })
        );
    } else {
      return this.getFormattedWidgetData(concept, widget, body);
    }
  }

  getFormattedWidgetData(concept: string, widget: DashboardWidget, body: CommonApiRequestBody): Observable<DashboardWidget> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    return this.http
      .post<BackendGroupingResponse>(`${this.environment.api.url}${this.environment.api.endpoints.widgets}/${concept}/search`, body, { params })
      .pipe(
        retry(1),
        catchError(() => of({ ...widget, data: [] } as DashboardWidget)),
        map((response: BackendGroupingResponse) => ({
          ...widget,
          data: response.data.filter((d) => d.count > 0).sort(SortUtil.sortBy('count', false)),
          options: { ...widget.options, ...response.options }
        }))
      );
  }
}
